export const MINI_BOSS_SECTOR = 5;
export const MINI_BOSS_HP = 500;
export const MINI_BOSS_SHARDS = 4;
export const GUARDIAN_PHASES = [
  'guardianReady',
  'guardianRolled',
  'guardianStrike',
];
export const getWardenShardReward = (attacks) =>
  attacks <= 3 ? 8 : attacks <= 6 ? 6 : 4;
export const getGuardianCounterDamage = (room) => (room.damage <= 2 ? 1 : 2);
export const createGuardian = (game, room) => ({
  enemy:
    game.floor === MINI_BOSS_SECTOR ? RIFT_WARDEN : getSectorMinion(game.floor),
  isWarden: game.floor === MINI_BOSS_SECTOR,
  hp: game.floor === MINI_BOSS_SECTOR ? MINI_BOSS_HP : room.target,
  maxHp: game.floor === MINI_BOSS_SECTOR ? MINI_BOSS_HP : room.target,
  round: 1,
  active: true,
  defeated: false,
  consumed: [],
  lastRound: null,
});
export const MINI_BOSS_PHASES = [
  'minibossReady',
  'minibossRolled',
  'minibossResult',
  'minibossReward',
];

export const SECTOR_MINIONS = [
  {
    id: 'static-wisp',
    name: 'Static Wisp',
    shape: 'wisp',
    color: '#79f5e6',
    signal: 'A stranded echo is learning the shape of your signal.',
  },
  {
    id: 'shardwing',
    name: 'Shardwing',
    shape: 'wing',
    color: '#a99bff',
    signal: 'Broken constellations gather beneath a pair of glass wings.',
  },
  {
    id: 'cache-maw',
    name: 'Cache Maw',
    shape: 'maw',
    color: '#ffc474',
    signal: 'The Core has taught the abandoned caches to hunger.',
  },
  {
    id: 'relay-stalker',
    name: 'Relay Stalker',
    shape: 'hound',
    color: '#6bcfff',
    signal: 'Something is following your pulse between the portals.',
  },
  {
    id: 'gate-sentry',
    name: 'Gate Sentry',
    shape: 'sentry',
    color: '#ffe083',
    signal: 'A sentry guards the last gate into the Warden’s territory.',
  },
  {
    id: 'null-serpent',
    name: 'Null Serpent',
    shape: 'serpent',
    color: '#ff899e',
    signal: 'The Warden has fallen. The Core is sending its older creations.',
  },
  {
    id: 'prism-widow',
    name: 'Prism Widow',
    shape: 'spider',
    color: '#d69dff',
    signal: 'Portal threads are becoming a web. Keep your signal moving.',
  },
  {
    id: 'signal-reaper',
    name: 'Signal Reaper',
    shape: 'reaper',
    color: '#aeef99',
    signal: 'A silent collector is harvesting the light of entire worlds.',
  },
  {
    id: 'fracture-knight',
    name: 'Fracture Knight',
    shape: 'knight',
    color: '#ff92db',
    signal: 'The Core’s last knight stands between you and the source.',
  },
];

export const RIFT_WARDEN = {
  id: 'rift-warden',
  name: 'The Rift Warden',
  shape: 'warden',
  color: '#ffb66d',
  signal:
    'The Core’s first great fragment seals the route to the inner galaxies.',
};

export const getSectorMinion = (sector) =>
  sector === MINI_BOSS_SECTOR
    ? RIFT_WARDEN
    : SECTOR_MINIONS[
        Math.max(0, Math.min(SECTOR_MINIONS.length - 1, sector - 1))
      ] || SECTOR_MINIONS[0];

export const isGuardianSaveValid = (game) => {
  if (!GUARDIAN_PHASES.includes(game.phase)) return true;
  const guardian = game.guardian;
  return (
    game.floor > 1 &&
    game.floor < 10 &&
    guardian?.active === true &&
    guardian.enemy?.id === getSectorMinion(game.floor).id &&
    guardian.isWarden === (game.floor === MINI_BOSS_SECTOR) &&
    Number.isSafeInteger(guardian.hp) &&
    guardian.hp >= 0 &&
    Number.isSafeInteger(guardian.maxHp) &&
    guardian.maxHp > 0 &&
    guardian.hp <= guardian.maxHp &&
    (!guardian.isWarden || guardian.maxHp === MINI_BOSS_HP) &&
    Number.isSafeInteger(guardian.round) &&
    guardian.round >= 1 &&
    Array.isArray(guardian.consumed) &&
    guardian.consumed.every(
      (index) => Number.isInteger(index) && index >= 0 && index < 25
    ) &&
    Array.isArray(game.doorBoard) &&
    game.doorBoard.length === 25 &&
    (game.phase === 'guardianStrike'
      ? !!guardian.lastRound &&
        Number.isSafeInteger(game.roll) &&
        Number.isInteger(game.landingIndex) &&
        game.landingIndex >= 0 &&
        game.landingIndex < 25 &&
        typeof guardian.lastRound.combatId === 'string' &&
        guardian.lastRound.enemyHpAfter === guardian.hp &&
        guardian.lastRound.attacks === guardian.round &&
        Number.isFinite(guardian.lastRound.enemyHpBefore) &&
        Number.isFinite(guardian.lastRound.playerHpBefore)
      : guardian.hp > 0 && game.hp > 0) &&
    (game.phase !== 'guardianRolled' ||
      game.roll === null ||
      Number.isSafeInteger(game.roll))
  );
};

export const getMiniBossRoom = (game) => ({
  id: 'rift-warden',
  name: RIFT_WARDEN.name,
  kind: 'Boss',
  target: game.miniBoss?.hp ?? MINI_BOSS_HP,
  damage: 0,
  shards: 0,
  heal: 0,
});

export const getMiniBossIntent = (game) => {
  const round = game.miniBoss?.round || 1;
  const enraged = (game.miniBoss?.hp ?? MINI_BOSS_HP) <= 250;
  return {
    name:
      round % 3 === 0
        ? 'Rift Cleave'
        : round % 2 === 0
        ? 'Static Lash'
        : 'Signal Bite',
    damage: (round % 3 === 0 ? 3 : 2) + (enraged ? 1 : 0),
    enraged,
  };
};

export const getGuardianCounterAttack = (enemy, round = 1) => {
  if (enemy?.shape === 'warden')
    return {
      name: getMiniBossIntent({ miniBoss: { round } }).name,
      rift: true,
      blades: round % 3 === 0 ? 5 : 3,
    };
  const attacks = {
    wisp: ['Static Splinters', 3],
    wing: ['Shard Volley', 5],
    maw: ['Fracture Fangs', 3],
    hound: ['Signal Talons', 3],
    serpent: ['Null Fangs', 3],
    spider: ['Prism Needles', 7],
    reaper: ['Signal Scythe', 3],
    knight: ['Fracture Lance', 1],
    sentry: ['Gatebreaker', 3],
  };
  const [name, blades] = attacks[enemy?.shape] || attacks.wisp;
  const telegraphs = {
    wisp: 'Static plasma coils into a charged constellation.',
    wing: 'Crystal feathers fan out into a sweeping volley.',
    maw: 'Fracture fangs open around a crushing bite.',
    hound: 'Three signal claws align for a crossing strike.',
    sentry: 'Hexagonal gates spin up a shockwave.',
    serpent: 'Null energy winds into three twisting coils.',
    spider: 'Prism threads draw a web of charged needles.',
    reaper: 'A crescent of stolen light sharpens into a scythe.',
    knight: 'A fracture lance locks onto your signal.',
  };
  return {
    name,
    blades,
    rift: false,
    telegraph: telegraphs[enemy?.shape] || telegraphs.wisp,
  };
};

export const isMiniBossSaveValid = (game) => {
  if (!MINI_BOSS_PHASES.includes(game.phase)) return true;
  const boss = game.miniBoss;
  return (
    game.floor === MINI_BOSS_SECTOR &&
    Number.isInteger(boss?.hp) &&
    boss.hp >= 0 &&
    boss.hp <= MINI_BOSS_HP &&
    Number.isInteger(boss.round) &&
    boss.round >= 1 &&
    !!boss.roomResult &&
    boss.roomResult.floor === MINI_BOSS_SECTOR &&
    Number.isFinite(boss.roomResult.target) &&
    Number.isFinite(boss.roomResult.effectiveRoll) &&
    Array.isArray(boss.offers) &&
    new Set(boss.offers).size === boss.offers.length &&
    (boss.consumed === undefined ||
      (Array.isArray(boss.consumed) &&
        boss.consumed.every(
          (index) => Number.isInteger(index) && index >= 0 && index < 25
        ))) &&
    Array.isArray(game.doorBoard) &&
    game.doorBoard.length === 25 &&
    (game.phase === 'minibossReward'
      ? boss.hp === 0 && boss.defeated && !boss.rewarded
      : boss.hp > 0 && !boss.defeated && boss.active) &&
    (game.phase !== 'minibossResult' || !!boss.lastRound) &&
    (game.phase !== 'minibossRolled' ||
      game.roll === null ||
      Number.isInteger(game.roll))
  );
};
